import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatPopularity } from "@/utils/movie";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const go = (index: number) => {
    setCurrent((index + movies.length) % movies.length);
  };

  const active = movies[current];

  return (
    <section className="relative w-full h-[440px] md:h-[560px] lg:h-[620px] overflow-hidden bg-primary-950">
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title}高清剧照`}
            title={`${movie.title} 激情丛林在线观看`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative h-full px-4 md:px-6 flex items-center">
        <div className="w-full max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4 animate-fade-up">
              <span className="bg-accent-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-md">
                精选推荐
              </span>
              <span className="text-background-100 text-xs font-medium">
                {active.year} · {active.type} · {active.genres.join(" / ")}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-background-50 leading-tight mb-4">
              {active.title}
            </h2>
            <p className="text-sm md:text-base text-background-100/90 leading-relaxed mb-6 max-w-xl line-clamp-3">
              {active.intro}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/detail/${active.id}`)}
                className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-bold text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </button>
              <Link
                to={`/category/${active.type === "电影" ? "movie" : "tv"}`}
                className="flex items-center gap-2 bg-background-50/15 hover:bg-background-50/25 backdrop-blur text-background-50 font-semibold text-sm px-6 py-3 rounded-md border border-background-50/30 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-list-check-2 text-lg" aria-hidden="true"></i>
                浏览同类
              </Link>
              <span className="flex items-center gap-1.5 text-background-100 text-sm ml-1">
                <i className="ri-fire-fill text-accent-400" aria-hidden="true"></i>
                {formatPopularity(active.popularity)} 次播放
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
        <button
          type="button"
          onClick={() => go(current - 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 cursor-pointer transition-colors"
          aria-label="上一部"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
        </button>
        <div className="flex items-center gap-2">
          {movies.map((movie, index) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => go(index)}
              aria-label={`切换到${movie.title}`}
              className={`h-1.5 rounded-full cursor-pointer transition-all ${
                index === current ? "w-7 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(current + 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 cursor-pointer transition-colors"
          aria-label="下一部"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}